// Main Application Entry Point
import './styles/index.css';
import './styles/loading.css';
import { router, navigateTo } from './router.js';
import { firebaseAuth, firebaseDB } from './firebase.js';
import { showToast } from './components/toast.js';
import { appState } from './store.js';
import { hideLoadingScreen } from './layout.js';
import { preloadManager, preloadCommonModules } from './utils/preload.js';

// Initialize app
async function initApp() {
  const app = document.getElementById('app');

  // Setup routes FIRST (before auth callback can trigger handleRoute)
  setupRoutes();

  // Setup auth state listener
  firebaseAuth.onAuthChange(async (user) => {
    appState.setState({ user });

    if (user) {
      // Get user data from Firestore
      try {
        const userData = await firebaseDB.getUser(user.uid);
        appState.setState({
          userData,
          isAdmin: userData?.role === 'admin'
        });

        // Preload common modules based on user role
        await preloadCommonModules(userData?.role || 'student');
      } catch (error) {
        console.error('Error fetching user data:', error);
        appState.setState({
          userData: null,
          isAdmin: false
        });
      }
    } else {
      appState.setState({
        userData: null,
        isAdmin: false
      });
    }

    appState.setState({ isLoading: false });
    hideLoadingScreen();

    // Handle routing after auth state is known
    router.handleRoute();
  });
}

// Setup all routes
function setupRoutes() {
  // Helper for lazy loading with preload capability
  const load = (importer, renderName) => async (params) => {
    try {
      // Check if module is preloaded
      let module = preloadManager.getPreloadedModule(importer);

      if (!module) {
        // If not preloaded, load it now
        module = await importer();
      }

      await module[renderName](params);
    } catch (error) {
      console.error('Failed to load page:', error);
      showToast('Không thể tải trang. Vui lòng thử lại.', 'error');
    }
  };

  // Auth routes
  router.register('/login', load(() => import('./pages/auth/login.js'), 'renderLoginPage'), { public: true });
  router.register('/register', load(() => import('./pages/auth/register.js'), 'renderRegisterPage'), { public: true });

  // Student routes
  router.register('/', load(() => import('./pages/student/dashboard.js'), 'renderStudentDashboard'), { auth: true });
  router.register('/dashboard', load(() => import('./pages/student/dashboard.js'), 'renderStudentDashboard'), { auth: true });
  router.register('/flashcards', load(() => import('./pages/student/flashcards.js'), 'renderFlashcardsPage'), { auth: true });
  router.register('/flashcards/:setId', load(() => import('./pages/student/flashcards.js'), 'renderFlashcardsPage'), { auth: true });
  router.register('/quiz', load(() => import('./pages/student/quiz.js'), 'renderQuizPage'), { auth: true });
  router.register('/quiz/:setId', load(() => import('./pages/student/quiz.js'), 'renderQuizPage'), { auth: true });
  router.register('/lessons', load(() => import('./pages/student/lessons.js'), 'renderLessonsPage'), { auth: true });
  router.register('/lessons/:lessonId', load(() => import('./pages/student/lessons.js'), 'renderLessonsPage'), { auth: true });
  router.register('/vocabulary', load(() => import('./pages/student/vocabulary.js'), 'renderVocabularyPage'), { auth: true });
  router.register('/leaderboard', load(() => import('./pages/student/leaderboard.js'), 'renderLeaderboardPage'), { auth: true });
  router.register('/profile', load(() => import('./pages/student/profile.js'), 'renderProfilePage'), { auth: true });

  // Admin routes
  router.register('/admin', load(() => import('./pages/admin/dashboard.js'), 'renderAdminDashboard'), { auth: true, admin: true });
  router.register('/admin/dashboard', load(() => import('./pages/admin/dashboard.js'), 'renderAdminDashboard'), { auth: true, admin: true });
  router.register('/admin/students', load(() => import('./pages/admin/students.js'), 'renderAdminStudents'), { auth: true, admin: true });
  router.register('/admin/content', load(() => import('./pages/admin/content.js'), 'renderAdminContent'), { auth: true, admin: true });
  router.register('/admin/assignments', load(() => import('./pages/admin/assignments.js'), 'renderAdminAssignments'), { auth: true, admin: true });
  router.register('/admin/settings', load(() => import('./pages/admin/settings.js'), 'renderAdminSettings'), { auth: true, admin: true });

  // Navigation guard
  router.setBeforeEach((route, path) => {
    // Still loading - wait
    if (appState.getState().isLoading) {
      return false;
    }

    const options = route.options || {};

    // Public routes - always accessible
    if (options.public) {
      // If logged in and trying to access login/register, redirect to dashboard
      if (appState.getState().user && (path === '/login' || path === '/register')) {
        navigateTo(appState.getState().isAdmin ? '/admin' : '/dashboard');
        return false;
      }
      return true;
    }

    // Protected routes - need auth
    if (options.auth && !appState.getState().user) {
      navigateTo('/login');
      return false;
    }

    // Admin routes - need admin role
    if (options.admin && !appState.getState().isAdmin) {
      showToast('Bạn không có quyền truy cập trang này', 'error');
      navigateTo('/dashboard');
      return false;
    }

    return true;
  });
}

// Start the app
initApp();